import {StyleSheet} from "aphrodite";
import * as React from "react";

import Clickable from "@khanacademy/wonder-blocks-clickable";
import {View} from "@khanacademy/wonder-blocks-core";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import Tooltip from "@khanacademy/wonder-blocks-tooltip";
import {LabelLarge} from "@khanacademy/wonder-blocks-typography";
import {mediaQueries} from "@khan/shared-styles";

export type Tab<T> = {
    disabled?: boolean;
    key: T;
    panelId: string;
    tabId: string;
    title: string;
    tooltipContents?: string;
};

type Props<T> = {
    tabs: Array<Tab<T>>;
    selectedTab: T;
    onSelectTab: (key: T) => void;
    // When true, will left-align the tab buttons on mobile
    mobileLeftAligned?: boolean;
};

export default function TabBar<T extends string>({
    tabs,
    selectedTab,
    onSelectTab,
    mobileLeftAligned,
}: Props<T>): React.ReactElement {
    const buttonsRef = React.useRef<Partial<Record<T, HTMLElement | null>>>({});
    // The focused tab might not be the selected tab if some tabs are disabled.
    const [focusedTab, setFocusedTab] = React.useState<T>(selectedTab);

    const selectTabByIndex = (tabIndex: number) => {
        const newTabKey = tabs[tabIndex].key;
        if (!tabs[tabIndex].disabled) {
            onSelectTab(newTabKey);
        }

        buttonsRef.current[newTabKey]?.focus();
        setFocusedTab(newTabKey);
    };

    const onKeyDown = (event: React.KeyboardEvent) => {
        let handled = true;
        const currentTabIndex = tabs.findIndex((tab) => tab.key === focusedTab);
        switch (event.key) {
            case "ArrowLeft":
                const previousTabIndex =
                    currentTabIndex === 0
                        ? tabs.length - 1
                        : currentTabIndex - 1;
                selectTabByIndex(previousTabIndex);
                break;
            case "ArrowRight":
                const nextTabIndex =
                    currentTabIndex === tabs.length - 1
                        ? 0
                        : currentTabIndex + 1;
                selectTabByIndex(nextTabIndex);
                break;
            case "Home":
                selectTabByIndex(0);
                break;
            case "End":
                selectTabByIndex(tabs.length - 1);
                break;
            default:
                handled = false;
                break;
        }

        if (handled) {
            event.preventDefault();
            event.stopPropagation();
        }
    };

    const alignmentStyles = mobileLeftAligned ? styles.leftAlignedTabRow : {};

    const tabButtons = tabs.map((tab) => {
        const isSelected = tab.key === selectedTab;
        const button = (
            <Clickable
                key={tab.key}
                ref={(r) =>
                    (buttonsRef.current[tab.key] =
                        r instanceof HTMLElement ? r : null)
                }
                id={tab.tabId}
                role="tab"
                aria-selected={isSelected ? "true" : "false"}
                aria-controls={tab.panelId}
                style={styles.tabButton}
                onClick={() => onSelectTab(tab.key)}
                tabIndex={isSelected ? 0 : -1}
                disabled={tab.disabled}
            >
                {() => (
                    <LabelLarge
                        style={[
                            styles.tabTitle,
                            isSelected ? styles.activeTab : styles.inactiveTab,
                        ]}
                    >
                        {tab.title}
                    </LabelLarge>
                )}
            </Clickable>
        );

        if (tab.tooltipContents != null) {
            return (
                <Tooltip key={tab.key} content={tab.tooltipContents}>
                    {button}
                </Tooltip>
            );
        }

        return button;
    });

    return (
        <View
            role="tablist"
            style={[styles.tabRow, alignmentStyles]}
            onKeyDown={onKeyDown}
        >
            {tabButtons}
        </View>
    );
}

const styles = StyleSheet.create({
    tabButton: {
        cursor: "pointer",
        paddingTop: spacing.xSmall_8,
        paddingBottom: 0,
        paddingLeft: 1,
        paddingRight: 1,
        border: "none",
        background: "transparent",
        display: "flex",
        flexDirection: "column",
    },

    activeTab: {
        color: color.blue,
        borderBottom: `4px solid ${color.blue}`,
    },

    inactiveTab: {
        ":hover": {
            borderBottom: `4px solid ${color.offBlack16}`,
            transition: "border-width 75ms ease-in-out",
        },
        borderBottom: `0px solid ${color.offBlack16}`,
        transition: "border-width 150ms ease-in-out",
    },

    tabRow: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        gap: 20,
        flexShrink: 0,
    },

    leftAlignedTabRow: {
        justifyContent: "flex-start",
        paddingLeft: 20,

        [mediaQueries.mdOrLarger]: {
            justifyContent: "center",
            paddingLeft: 0,
        },
    },

    tabTitle: {
        fontSize: 14,
        lineHeight: "18px",
        paddingBottom: spacing.xxxSmall_4,
    },
});
